/**
 * Klickbarer Spaltenkopf. Sieht aus wie die bisherige Überschrift — bis man
 * mit der Maus darüber geht.
 *
 * Der Pfeil steht NUR an der aktiven Spalte. Ein Pfeilpaar an jeder Spalte
 * (das übliche Muster) würde acht Spaltenköpfe mit Symbolen zupflastern, und
 * die Kopfzeile ist hier ohnehin schon eng.
 *
 * Mit `info` steht neben der Überschrift ein Info-Symbol mit dem Erklärtext
 * aus `lib/glossar.ts` (Marie, 16.09.2026). Es sitzt NEBEN der Schaltfläche,
 * nicht darin: Eine Schaltfläche in einer Schaltfläche ist ungültig, und ein
 * Klick aufs Symbol soll nicht sortieren.
 */
import { ChevronDown, ChevronUp } from "lucide-react";
import type { Richtung } from "../../lib/tabellen-sortierung.js";
import { KpiInfo } from "./KpiInfo.js";

export function SortKopf({
  label,
  spalte,
  richtung,
  onKlick,
  rechts,
  zusatz,
  info,
}: {
  label: string;
  spalte: string;
  /** null = diese Spalte sortiert gerade nicht. */
  richtung: Richtung | null;
  onKlick: (spalte: string) => void;
  /** Zahlenspalten stehen rechtsbündig — der Pfeil muss mitwandern. */
  rechts?: boolean | undefined;
  /** Blasser Zusatz wie „↗ Detail", der nicht Teil des Sortier-Labels ist. */
  zusatz?: string | undefined;
  /** Schlüssel in `lib/glossar.ts`; ohne ihn bleibt die Spalte ohne Info-Symbol. */
  info?: string | undefined;
}) {
  const aktiv = richtung !== null;
  const knopf = (
    <button
      type="button"
      onClick={() => onKlick(spalte)}
      // aria-sort gehört eigentlich an die Kopfzelle. Diese Tabellen sind aus
      // Grid-<div>s gebaut, nicht aus <table> — also sitzt es hier, damit
      // Screenreader die Richtung überhaupt erfahren.
      aria-sort={!aktiv ? "none" : richtung === "auf" ? "ascending" : "descending"}
      title={`Nach ${label} sortieren`}
      className={`flex items-center gap-1 min-w-0 text-xs font-medium transition-colors sort-kopf ${
        rechts ? "justify-end" : ""
      }`}
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        font: "inherit",
        color: aktiv ? "var(--text-primary)" : "inherit",
        textAlign: rechts ? "right" : "left",
      }}
    >
      <span className="truncate">
        {label}
        {zusatz && <span style={{ opacity: 0.4, fontWeight: 400 }}> {zusatz}</span>}
      </span>
      {aktiv &&
        (richtung === "auf" ? (
          <ChevronUp size={11} aria-hidden className="shrink-0" />
        ) : (
          <ChevronDown size={11} aria-hidden className="shrink-0" />
        ))}
    </button>
  );

  if (!info) return knopf;
  return (
    <span className={`flex items-center gap-0.5 min-w-0 ${rechts ? "justify-end" : ""}`}>
      {knopf}
      <KpiInfo schluessel={info} label={label} />
    </span>
  );
}
